import { CheckCircle2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { CrmDataset } from '../../types/crm'
import { ActivityHistory } from './ActivityHistory'
import {
  submitMissingInformationUndo,
} from '../review-missing-information/writebackClient'
import { getMissingInformationFields } from '../review-missing-information/missingInformation'
import {
  DASHBOARD_ACTIVITY_EVENT,
  getTodayDashboardActivitySummary,
  removeDashboardActivity,
  type DashboardActivityEntry,
  type DashboardActivitySummary,
} from './dashboardActivity'

function formatActivityTime(
  timestamp: number,
): string {
  const elapsed =
    Math.max(0, Date.now() - timestamp)

  const minutes =
    Math.floor(elapsed / 60_000)

  if (minutes < 1) return 'just now'
  if (minutes === 1) return '1 min ago'
  if (minutes < 60) {
    return `${minutes} min ago`
  }

  const hours = Math.floor(minutes / 60)

  if (hours === 1) return '1 hr ago'

  return `${hours} hrs ago`
}

export function AttentionToday({
  dataset,
}: {
  dataset: CrmDataset
}) {
  const [activity, setActivity] =
    useState<DashboardActivitySummary>(
      () =>
        getTodayDashboardActivitySummary(),
    )

  const [
    undoingActivityId,
    setUndoingActivityId,
  ] = useState<string | null>(null)

  const [
    undoSucceededActivityId,
    setUndoSucceededActivityId,
  ] = useState<string | null>(null)

  const [
    progressMessage,
    setProgressMessage,
  ] = useState('')

  useEffect(() => {
    function refreshActivity() {
      setActivity(
        getTodayDashboardActivitySummary(),
      )
    }

    window.addEventListener(
      DASHBOARD_ACTIVITY_EVENT,
      refreshActivity,
    )

    window.addEventListener(
      'storage',
      refreshActivity,
    )

    return () => {
      window.removeEventListener(
        DASHBOARD_ACTIVITY_EVENT,
        refreshActivity,
      )

      window.removeEventListener(
        'storage',
        refreshActivity,
      )
    }
  }, [])

  async function undoActivity(
    entry: DashboardActivityEntry,
  ) {
    if (
      !entry.undoToken ||
      undoingActivityId
    ) {
      return
    }

    setUndoSucceededActivityId(null)
    setUndoingActivityId(entry.id)
    setProgressMessage('')

    try {
      await submitMissingInformationUndo(
        entry.undoToken,
      )

      setUndoSucceededActivityId(entry.id)

      await new Promise<void>((resolve) =>
        window.setTimeout(resolve, 650),
      )

      removeDashboardActivity(entry.id)

      window.dispatchEvent(
        new CustomEvent(
          'wolf-forge-crm-refresh',
        ),
      )

      setProgressMessage(
        `Update undone for ${entry.company}.`,
      )
    } catch (cause) {
      setProgressMessage(
        cause instanceof Error
          ? cause.message
          : 'This update could not be undone.',
      )
    } finally {
      setUndoingActivityId(null)
      setUndoSucceededActivityId(null)
    }
  }

  const analyzed = dataset.leads.map(
    (lead) => {
      const fields =
        getMissingInformationFields(lead)

      const keys = new Set(
        fields.map((field) => field.key),
      )

      const missingOwner =
        !lead.ownerIdentified

      const missingContact =
        keys.has('email') &&
        keys.has('phone')

      const missingWebsite =
        keys.has('website')

      const missingLocation =
        keys.has('city') ||
        keys.has('state')

      const missingNotes =
        keys.has('notes')

      const ownerExtra =
        missingOwner &&
        !keys.has('owner')
          ? 1
          : 0

      const missingCount =
        fields.length + ownerExtra

      return {
        missingOwner,
        missingContact,
        missingWebsite,
        missingLocation,
        missingNotes,
        missingCount,
      }
    },
  )

  const issues = [
    {
      label: 'Missing Owners',
      count: analyzed.filter(
        (item) => item.missingOwner,
      ).length,
      description:
        'Prospects without an identified decision-maker.',
      tone: 'critical',
    },
    {
      label: 'Missing Contact Info',
      count: analyzed.filter(
        (item) => item.missingContact,
      ).length,
      description:
        'Both email and phone are missing.',
      tone: 'high',
    },
    {
      label: 'Missing Website',
      count: analyzed.filter(
        (item) => item.missingWebsite,
      ).length,
      description:
        'No usable company website is recorded.',
      tone: 'medium',
    },
    {
      label: 'Missing Location',
      count: analyzed.filter(
        (item) => item.missingLocation,
      ).length,
      description:
        'City or state information is incomplete.',
      tone: 'medium',
    },
    {
      label: 'Missing Notes',
      count: analyzed.filter(
        (item) => item.missingNotes,
      ).length,
      description:
        'No useful CRM notes are recorded yet.',
      tone: 'medium',
    },
  ]

  const prospectsNeedingAttention =
    analyzed.filter(
      (item) => item.missingCount > 0,
    )

  return (
    <section
      aria-labelledby="attention-today-title"
      className="attention-panel"
    >
      <header className="attention-panel-header">
        <div>
          <span className="attention-eyebrow">
            Action queue
          </span>

          <h2 id="attention-today-title">
            Here's what needs your attention
            today,{' '}
            <span className="attention-name">
              William
            </span>
            .
          </h2>

          <p>
            Live CRM gaps that can slow down
            prospecting and follow-up.
          </p>
        </div>

        <div className="attention-total">
          <strong>
            {
              prospectsNeedingAttention.length
            }
          </strong>

          <span>prospects need review</span>
        </div>
      </header>

      <div className="attention-grid">
        {issues
          .filter(
            (issue) => issue.count > 0,
          )
          .map((issue) => (
            <article
              className={`attention-card is-${issue.tone}`}
              key={issue.label}
            >
              <div className="attention-card-top">
                <span aria-hidden="true" />
                <strong>
                  {issue.count}
                </strong>
              </div>

              <h3>{issue.label}</h3>
              <p>{issue.description}</p>

              <button
                className="attention-take-action"
                onClick={(event) => {
                  window.dispatchEvent(
                    new CustomEvent(
                      'wolf-forge-dashboard-review',
                      {
                        detail: {
                          trigger:
                            event.currentTarget,
                          label: issue.label,
                        },
                      },
                    ),
                  )
                }}
                type="button"
              >
                Take action {'\u2192'}
              </button>
            </article>
          ))}
      </div>

      <section className="today-progress">
        <div className="today-progress-header">
          <div>
            <span>Today's progress</span>

            <h3>
              Good work,{' '}
              <span className="attention-name">
                William
              </span>
              .
            </h3>
          </div>

          <p>
            Progress from today's completed
            CRM actions.
          </p>
        </div>

        <div className="today-progress-metrics">
          <div>
            <strong>
              {activity.fieldsResolved}
            </strong>

            <span>
              fields resolved today
            </span>
          </div>

          <div>
            <strong>
              {activity.prospectsUpdated}
            </strong>

            <span>
              prospects updated today
            </span>
          </div>
        </div>

        <div className="today-progress-recent">
          <div className="today-progress-recent-heading">
            <span>Recently updated</span>
          </div>

          {progressMessage ? (
            <div className="today-progress-message">
              {progressMessage}
            </div>
          ) : null}

          {activity.recent.length > 0 ? (
            <div className="today-progress-list">
              {activity.recent.map(
                (entry) => (
                  <div
                    className="today-progress-row"
                    key={entry.id}
                  >
                    <span className="today-progress-check">
                      <CheckCircle2
                        aria-hidden="true"
                        size={18}
                      />
                    </span>

                    <div>
                      <strong>
                        {entry.company}
                      </strong>

                      <span>
                        {entry.action} {'\u2022'}{' '}
                        {formatActivityTime(
                          entry.timestamp,
                        )}
                      </span>
                    </div>

                    {entry.undoToken ? (
                      <button
                        className="today-progress-undo"
                        disabled={
                          undoingActivityId ===
                          entry.id
                        }
                        onClick={() =>
                          undoActivity(entry)
                        }
                        type="button"
                      >
                        {undoingActivityId ===
                          entry.id ? (
                            <span
                              aria-label={
                                undoSucceededActivityId ===
                                entry.id
                                  ? 'Undone'
                                  : 'Undoing'
                              }
                              className={`wf-action-state ${
                                undoSucceededActivityId ===
                                entry.id
                                  ? 'is-success'
                                  : 'is-loading'
                              }`}
                            >
                              <span
                                aria-hidden="true"
                                className="wf-action-state__rail"
                              >
                                <span />
                              </span>

                              <span
                                aria-hidden="true"
                                className="wf-action-state__check"
                              />
                            </span>
                          ) : (
                            'Undo'
                          )}
                      </button>
                    ) : null}
                  </div>
                ),
              )}
            </div>
          ) : (
            <div className="today-progress-empty">
              <span>
                No CRM actions completed yet
                today.
              </span>

              <small>
                Completed Take Action updates
                will appear here automatically.
              </small>
            </div>
          )}
        </div>
      </section>
      <ActivityHistory dataset={dataset} />
    </section>
  )
}

